import "./styles/globals.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./app/App";
import { DevTools } from "./app/DevTools";
import { MsalProvider } from "@azure/msal-react";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import MsalInstance from "./services/authClient";

async function initMsal() {
	await MsalInstance.initialize();
}

initMsal();

const root = createRoot(document.getElementById("root") as HTMLElement);

const queryClient = new QueryClient({
	defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } }
});

// `useAvatar` caches a `blob:` URL produced by `URL.createObjectURL` against the
// query data. Without explicit revocation those URLs leak for the lifetime of
// the page. We can't safely revoke on component unmount because the URL is
// shared across every consumer of the same `["avatar", userId]` query — a
// sibling component may still need it. Hooking into React Query's cache lets
// us revoke at the two moments where no consumer can still hold a reference:
//   1. `updated` — the query's data was replaced (e.g. refetch after staleTime
//      expiry). The previous URL is no longer reachable through the cache, so
//      revoke it.
//   2. `removed` — the entry was evicted (gcTime expiry, manual cache clear on
//      sign-out). Revoke whatever URL we last tracked for that entry.
const trackedAvatarUrls = new Map<string, string>();
queryClient.getQueryCache().subscribe((event) => {
	const [scope] = event.query.queryKey as readonly unknown[];
	if (scope !== "avatar") return;

	const hash = event.query.queryHash;
	const previous = trackedAvatarUrls.get(hash);
	const data = event.query.state.data;
	const currentUrl =
		typeof data === "string" && data.startsWith("blob:") ? data : undefined;

	if (event.type === "removed") {
		if (previous) URL.revokeObjectURL(previous);
		trackedAvatarUrls.delete(hash);
		return;
	}

	// `added` / `updated`: revoke a previously-tracked URL that's been replaced.
	if (previous && previous !== currentUrl) {
		URL.revokeObjectURL(previous);
	}
	if (currentUrl) {
		trackedAvatarUrls.set(hash, currentUrl);
	} else {
		trackedAvatarUrls.delete(hash);
	}
});

const appInsightsConnString = window._env_.REACT_APP_AI_CONNSTRING;

if (appInsightsConnString) {
	const appInsights = new ApplicationInsights({
		config: {
			connectionString: appInsightsConnString.replace("%3D", "=") // we have to escape the = because of how we load in the vars at build time,
		}
	});
	appInsights.loadAppInsights();
	appInsights.trackPageView(); // This only tacks a full page load, no react routing
}

root.render(
	<BrowserRouter basename={window._env_.PUBLIC_URL}>
		<QueryClientProvider client={queryClient}>
			<MsalProvider instance={MsalInstance}>
				<App />
			</MsalProvider>
			<DevTools />
		</QueryClientProvider>
	</BrowserRouter>
);
